import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheckCircle, IconInfoFilled, IconWarning, IconX } from '@components/icons';
import { DEFAULT_INTERVAL } from '@contexts/toast/toast-context';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';

import { Props } from './notification.type';
import { getNotificationClasses } from './utils';

export const Notification = ({
  id,
  title,
  text,
  onRemove,
  lifetime = DEFAULT_INTERVAL,
  type = 'error',
  disableTimeOut = false,
  neutralBg = false,
}: Props) => {
  const { t } = useTranslation();
  const [show, setIsShow] = useState(true);
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    if (!disableTimeOut) {
      const start = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - start;
        setTimeElapsed(Math.min(elapsed, lifetime));
      }, 100);

      const timeout = setTimeout(() => {
        onRemove?.(id);
      }, lifetime);

      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }
  }, [id, lifetime, onRemove, disableTimeOut]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (!show) {
      timeout = setTimeout(() => {
        onRemove?.(id);
      }, 150);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [id, onRemove, show]);

  const handleRemove = () => {
    setIsShow(false);
  };

  const progressWidth = disableTimeOut ? '100%' : `${(timeElapsed / lifetime) * 100}%`;

  const { containerClasses, progressClasses, buttonClasses, iconClasses, textContainerClasses } =
    getNotificationClasses(type, neutralBg);

  return (
    <Transition
      show={show}
      as={Fragment}
      enter='ease-out duration-300 transition'
      enterFrom='translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2'
      enterTo='translate-y-0 opacity-100 sm:translate-x-0'
      leave='transition ease-in duration-100'
      leaveFrom='opacity-100'
      leaveTo='opacity-0'>
      <div className={classNames(containerClasses)}>
        {!disableTimeOut && <div className={classNames(progressClasses)} style={{ width: progressWidth }} />}
        <div className='p-4'>
          <div className='flex items-center'>
            <div className='shrink-0'>
              {type === 'success' && <IconCheckCircle aria-hidden='true' className={iconClasses} />}
              {type === 'warning' && <IconWarning aria-hidden='true' className={iconClasses} />}
              {type === 'error' && <IconWarning aria-hidden='true' className={iconClasses} />}
              {type === 'info' && <IconInfoFilled aria-hidden='true' className={iconClasses} />}
            </div>
            <div className={textContainerClasses}>
              <p className='text-subtitle-s'>{title}</p>
              <p className='text-body-s'>{text}</p>
            </div>
            <div className='ml-4 shrink-0 flex'>
              <button className={buttonClasses} onClick={handleRemove}>
                <span className='sr-only'>{t('common:closeLabel')}</span>
                <IconX className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};
